import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GraduationCap, Award, Play, Download, Eye, Loader, CheckCircle, AlertTriangle, ChevronLeft, Smartphone, Languages, BookOpen } from 'lucide-react';

// Driver portal "Training" tab.
//   • Required — retraining the office has assigned (must be passed).
//   • Available — the self-serve library of active quizzes.
//   • My certificates — competency certificates already earned (download / view).
// Take-flow: pick a language, then answer one multiple-choice question at a time.
// A WRONG answer flashes the screen red and restarts from question one. Passing
// every question issues a Competency Certificate (kept here and in My Docs).

const API = '/.netlify/functions/driver-training-sb';

// Language selector. All four are LIVE: quiz text is stored as a language map, and
// a topic that has not been translated yet offers the driver a button that
// translates it there and then (cached on the quiz, so only the first person waits).
// Until that happens the quiz still runs — in English — so nobody is ever blocked.
const LANGS = [
  { code: 'en', lang: 'English',   flag: '🇦🇺' },
  { code: 'es', lang: 'Español',   flag: '🇪🇸' },
  { code: 'zh', lang: '中文',       flag: '🇨🇳' },
  { code: 'mn', lang: 'Монгол',    flag: '🇲🇳' },
];
// Shared with the safety portal and the security declaration, on purpose: a driver
// picks the language they read ONCE and every compulsory read honours it.
const LANG_KEY = 'hp_safety_lang';
const getLang = () => { try { return localStorage.getItem(LANG_KEY) || 'en'; } catch { return 'en'; } };
const setStoredLang = (c) => { try { localStorage.setItem(LANG_KEY, c); } catch {} };

// The chrome around the questions. The quiz CONTENT is translated by the engine;
// these are the words the screen says on its own, and a driver reading a Spanish
// quiz through an English frame is only half-served.
const UI = {
  en: { chooseLang: 'Choose your language', notTranslated: 'Not translated yet — English for now',
        needsUpdate: 'The English changed — translate it again', translateBtn: 'Translate this quiz',
        translating: 'Translating', translateHint: 'Up to a minute. Only the first person ever waits.',
        rules: (n) => `${n} questions. You must answer every question correctly — a wrong answer restarts the quiz. Pass to earn your certificate.`,
        start: 'Start', guide: 'Read the illustrated guide first', practice: 'Have a go on a practice scanner first',
        question: (i, n) => `Question ${i} of ${n}`, quit: 'Quit', back: 'Back',
        wrong: 'Wrong Answer', again: 'Please start again', issuing: 'Issuing your certificate…',
        passedTitle: 'Competency achieved', certNo: 'Certificate', backToTraining: 'Back to training',
        passedBody: (topic) => <>You passed <b style={{ color: 'var(--hp-text)' }}>{topic}</b>. Your certificate is saved to your Training tab and your My Docs.</> },
  es: { chooseLang: 'Elige tu idioma', notTranslated: 'Sin traducir todavía — en inglés por ahora',
        needsUpdate: 'El inglés cambió — vuelve a traducirlo', translateBtn: 'Traducir este cuestionario',
        translating: 'Traduciendo', translateHint: 'Hasta un minuto. Solo espera la primera persona.',
        rules: (n) => `${n} preguntas. Todas deben ser correctas — una respuesta incorrecta reinicia el cuestionario. Apruébalo para obtener tu certificado.`,
        start: 'Empezar', guide: 'Lee primero la guía ilustrada', practice: 'Practica primero en un escáner de prueba',
        question: (i, n) => `Pregunta ${i} de ${n}`, quit: 'Salir', back: 'Atrás',
        wrong: 'Respuesta incorrecta', again: 'Empieza de nuevo, por favor', issuing: 'Emitiendo tu certificado…',
        passedTitle: 'Competencia lograda', certNo: 'Certificado', backToTraining: 'Volver a formación',
        passedBody: (topic) => <>Aprobaste <b style={{ color: 'var(--hp-text)' }}>{topic}</b>. Tu certificado está guardado en tu pestaña de formación y en Mis Documentos.</> },
  zh: { chooseLang: '选择你的语言', notTranslated: '尚未翻译 — 暂时显示英文',
        needsUpdate: '英文内容已更新 — 请重新翻译', translateBtn: '翻译本测验',
        translating: '翻译中', translateHint: '最多约一分钟。只有第一个人需要等待。',
        rules: (n) => `共 ${n} 题。每题都必须答对 — 答错一题即从第一题重新开始。全部答对即可获得证书。`,
        start: '开始', guide: '请先阅读图解指南', practice: '先用练习扫描枪试一试',
        question: (i, n) => `第 ${i} 题 / 共 ${n} 题`, quit: '退出', back: '返回',
        wrong: '答错了', again: '请重新开始', issuing: '正在生成你的证书…',
        passedTitle: '已通过考核', certNo: '证书', backToTraining: '返回培训',
        passedBody: (topic) => <>你通过了 <b style={{ color: 'var(--hp-text)' }}>{topic}</b>。证书已保存在你的培训页面和「我的文件」中。</> },
  mn: { chooseLang: 'Хэлээ сонгоно уу', notTranslated: 'Хараахан орчуулаагүй — одоогоор англиар',
        needsUpdate: 'Англи эх нь өөрчлөгдсөн — дахин орчуулна уу', translateBtn: 'Энэ шалгалтыг орчуулах',
        translating: 'Орчуулж байна', translateHint: 'Нэг минут хүртэл. Зөвхөн хамгийн эхний хүн хүлээнэ.',
        rules: (n) => `${n} асуулт. Бүх хариулт зөв байх ёстой — буруу хариулбал эхнээс нь дахин эхэлнэ. Тэнцвэл гэрчилгээ авна.`,
        start: 'Эхлэх', guide: 'Эхлээд зурагт зааврыг уншина уу', practice: 'Эхлээд дадлагын сканнер дээр туршиж үзээрэй',
        question: (i, n) => `${i} / ${n} асуулт`, quit: 'Гарах', back: 'Буцах',
        wrong: 'Буруу хариулт', again: 'Эхнээс нь дахин эхэлнэ үү', issuing: 'Таны гэрчилгээг гаргаж байна…',
        passedTitle: 'Чадвараа баталгаажууллаа', certNo: 'Гэрчилгээ', backToTraining: 'Сургалт руу буцах',
        passedBody: (topic) => <><b style={{ color: 'var(--hp-text)' }}>{topic}</b> шалгалтыг тэнцлээ. Гэрчилгээ тань Сургалт таб болон Миний бичиг баримт дотор хадгалагдсан.</> },
};
const T = (lang, k) => (UI[lang] || UI.en)[k] !== undefined ? (UI[lang] || UI.en)[k] : UI.en[k];

const pick = (textMap, lang) => (textMap && typeof textMap === 'object') ? (textMap[lang] || textMap.en || Object.values(textMap)[0] || '') : (textMap || '');

// The illustrated guides are published once per language — /vehicle-security.html in
// English, /vehicle-security.es.html in Spanish. KEEP IN SYNC with TRANSLATED_GUIDES
// in SafetyPortal.jsx and SAFETY_GUIDES in netlify/edge-functions/password-gate.js:
// a guessed slug that does not exist is a 404 in a driver's hand.
const TRANSLATED_GUIDES = [
  'dealing-with-dogs', 'trams-and-tracks', 'vans-and-cars', 'manual-handling', 'fatigue',
  'unsafe-delivery-points', 'heat-and-uv', 'inside-the-facility', 'working-alone',
  'load-shifting-equipment', 'vehicle-security',
];
function guideHref(url, lang) {
  if (!url || !lang || lang === 'en') return url || '';
  const m = String(url).match(/^\/([a-z0-9-]+)(?:\.html)?$/);
  if (!m || !TRANSLATED_GUIDES.includes(m[1])) return url;
  return `/${m[1]}.${lang}.html`;
}

// 'ready' · 'stale' · 'missing' for the language the driver picked. The backend
// sends langState with every quiz; fall back to reading the maps if it is absent.
function stateOf(quiz, code) {
  if (code === 'en') return 'ready';
  if (quiz && quiz.langState && quiz.langState[code]) return quiz.langState[code];
  const qs = (quiz && quiz.questions) || [];
  const complete = qs.length > 0 && qs.every((q) => (q.prompt || {})[code] && (q.options || []).every((o) => (o.text || {})[code]));
  return complete ? 'ready' : 'missing';
}

function fmtWhen(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}
function b64ToBlob(base64, type) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: type || 'application/pdf' });
}
// Fisher–Yates; keeps the original index so we can report the answer to the server.
function shuffledWithIndex(options) {
  const arr = (options || []).map((o, i) => ({ o, i }));
  for (let k = arr.length - 1; k > 0; k--) {
    const j = Math.floor(Math.random() * (k + 1));
    [arr[k], arr[j]] = [arr[j], arr[k]];
  }
  return arr;
}

export default function TrainingPortalTab({ shift, onCountChange }) {
  const [data, setData] = useState(null);        // { assignments, quizzes, certificates, outstanding }
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  // take-flow
  const [view, setView] = useState('list');       // 'list' | 'lang' | 'quiz' | 'passed'
  const [quiz, setQuiz] = useState(null);
  const [lang, setLang] = useState(getLang);
  const [translating, setTranslating] = useState(null);  // { done, total } while a translation runs
  const [transErr, setTransErr] = useState('');
  const [qIdx, setQIdx] = useState(0);
  const [attempt, setAttempt] = useState(0);       // increments on each restart → reshuffles
  const [answers, setAnswers] = useState({});
  const [flashWrong, setFlashWrong] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passResult, setPassResult] = useState(null);

  const api = useCallback(async (payload) => {
    const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    return res.json();
  }, []);

  const load = useCallback(async () => {
    try {
      const d = await api({ action: 'getMyTraining' });
      if (d && d.success) { setData(d); onCountChange && onCountChange(d.outstanding || 0); }
      else { setData({ assignments: [], quizzes: [], certificates: [] }); setError(d?.error || 'Could not load training'); }
    } catch (e) { setData({ assignments: [], quizzes: [], certificates: [] }); setError(e.message); }
  }, [api, onCountChange]);

  useEffect(() => { load(); }, [load]);

  // The language the QUESTIONS are shown in. Only a complete, current translation
  // counts: a Spanish question translated from English that has since been rewritten
  // no longer matches the answers the server marks, so it falls back to English
  // until someone translates it again. The chrome stays in the driver's language.
  const contentLang = (quiz && stateOf(quiz, lang) === 'ready') ? lang : 'en';

  // Options for the current question, shuffled once per (question, attempt).
  const currentOptions = useMemo(() => {
    if (!quiz || !quiz.questions[qIdx]) return [];
    return shuffledWithIndex(quiz.questions[qIdx].options);
  }, [quiz, qIdx, attempt]);

  const startQuiz = (q) => { setQuiz(q); setLang(getLang()); setTransErr(''); setView('lang'); };
  const beginQuestions = () => { setQIdx(0); setAnswers({}); setAttempt((a) => a + 1); setView('quiz'); };
  const chooseLang = (code) => { setLang(code); setStoredLang(code); setTransErr(''); };

  // Translate the quiz the driver is looking at into the language they picked. The
  // backend answers a SLICE at a time (a whole quiz in one call runs too close to the
  // function timeout), so keep asking for the next slice until it says done — passing
  // its token back, which is what stops two drivers paying for the same translation.
  const translateThis = async () => {
    if (!quiz || lang === 'en' || translating) return;
    setTransErr(''); setTranslating({ done: 0, total: quiz.questions.length });
    try {
      let from = 0, token = '', guard = 0, latest = quiz, retried = false;
      for (;;) {
        const d = await api({ action: 'requestTranslation', quizId: quiz.id, lang, from, token });
        if (!d || !d.success) {
          // One retry for this slice: the model occasionally times out or returns
          // something unparseable, and a second ask almost always lands.
          if (!retried && !(d && d.busy)) { retried = true; continue; }
          setTransErr((d && d.error) || 'Could not translate.'); break;
        }
        retried = false;
        if (d.quiz) { latest = d.quiz; setQuiz(d.quiz); }
        setTranslating({ done: d.translated || quiz.questions.length, total: d.total || quiz.questions.length });
        if (d.done || d.cached) break;
        from = d.next; token = d.token || '';
        if (++guard > 20) break;   // slices are 2-4 questions; no real quiz needs 20 rounds
      }
      // Keep the list copy in step, so backing out and starting again is instant.
      setData((prev) => (prev ? { ...prev, quizzes: (prev.quizzes || []).map((q) => (q.id === latest.id ? latest : q)) } : prev));
    } catch (e) {
      setTransErr('Could not translate — check your signal.');
    } finally { setTranslating(null); }
  };
  const backToList = () => { setView('list'); setQuiz(null); setPassResult(null); };

  const answer = async (opt) => {
    if (flashWrong || submitting) return;
    const q = quiz.questions[qIdx];
    if (!opt.o.correct) {
      // Wrong → flash red, then restart the whole quiz from question one.
      setFlashWrong(true);
      setTimeout(() => { setFlashWrong(false); setQIdx(0); setAnswers({}); setAttempt((a) => a + 1); }, 1500);
      return;
    }
    const nextAnswers = { ...answers, [q.id]: opt.i };
    setAnswers(nextAnswers);
    if (qIdx + 1 < quiz.questions.length) { setQIdx(qIdx + 1); return; }
    // Last question answered correctly → submit for the certificate.
    setSubmitting(true); setError('');
    try {
      const d = await api({ action: 'submitResult', quizId: quiz.id, answers: nextAnswers });
      if (d && d.success) { setPassResult(d); setView('passed'); await load(); }
      else { setError(d?.error || 'Could not record your result'); setView('list'); }
    } catch (e) { setError(e.message); setView('list'); }
    finally { setSubmitting(false); }
  };

  const openCert = async (rec, mode) => {
    setBusyId(rec.id + mode); setError('');
    const w = mode === 'view' ? window.open('', '_blank') : null;
    try {
      const d = await api({ action: 'getCert', recordId: rec.id });
      if (!d || !d.success) throw new Error(d?.error || 'Could not open certificate');
      const url = URL.createObjectURL(b64ToBlob(d.base64, d.contentType));
      if (mode === 'view') { if (w) w.location.href = url; else window.open(url, '_blank'); }
      else { const a = document.createElement('a'); a.href = url; a.download = d.filename || 'certificate.pdf'; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); setError(e.message); }
    finally { setBusyId(''); }
  };

  if (data === null) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={16} /> Loading your training…</div>;

  // ── Take-flow: language chooser ─────────────────────────────────────────────
  if (view === 'lang' && quiz) {
    return (
      <div style={{ width: '100%' }}>
        <BackBar onClick={backToList} label={T(lang, 'back')} />
        <Title icon={<GraduationCap size={18} color="var(--hp-gold)" />} text={quiz.topic} />
        {pick(quiz.description, contentLang) && <p style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', marginBottom: 16 }}>{pick(quiz.description, contentLang)}</p>}
        <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 10 }}>{T(lang, 'chooseLang')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          {LANGS.map((L) => {
            const st = stateOf(quiz, L.code);
            return (
              <button key={L.code} onClick={() => chooseLang(L.code)} disabled={!!translating}
                style={{ padding: '14px 12px', borderRadius: 'var(--radius)', cursor: translating ? 'wait' : 'pointer',
                  border: `2px solid ${lang === L.code ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                  background: lang === L.code ? 'rgba(201,168,76,0.10)' : 'var(--hp-surface)', color: 'var(--hp-text)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: '1.4rem' }}>{L.flag}</span>
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{L.lang}</span>
                {st !== 'ready' && (
                  <span style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textAlign: 'center', lineHeight: 1.25 }}>
                    {T(L.code, st === 'stale' ? 'needsUpdate' : 'notTranslated')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Not translated yet? The driver makes one, right here — they should not have
            to wait for the office to notice. English still works in the meantime. */}
        {lang !== 'en' && stateOf(quiz, lang) !== 'ready' && (
          <div style={{ marginBottom: 16 }}>
            <button onClick={translateThis} disabled={!!translating}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 'var(--radius)', cursor: translating ? 'wait' : 'pointer',
                border: '1px solid var(--hp-gold)', background: 'rgba(201,168,76,0.08)', color: 'var(--hp-gold)',
                fontWeight: 700, fontSize: '0.85rem', fontFamily: 'var(--font-body)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              <Languages size={15} />
              {translating ? `${T(lang, 'translating')}… ${translating.done}/${translating.total}` : T(lang, 'translateBtn')}
            </button>
            <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 5, textAlign: 'center' }}>{T(lang, 'translateHint')}</div>
            {transErr && <div style={{ fontSize: '0.74rem', color: 'var(--hp-red)', marginTop: 5, textAlign: 'center' }}>{transErr}</div>}
          </div>
        )}

        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
          {T(lang, 'rules')(quiz.questions.length)}
        </div>
        {quiz.guideUrl && (
          <a href={guideHref(quiz.guideUrl, lang)} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', marginBottom: 10,
              padding: '12px 14px', borderRadius: 'var(--radius)', textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem',
              border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)' }}>
            <BookOpen size={15} /> {T(lang, 'guide')}
          </a>
        )}
        {quiz.practiceUrl && (
          <a href={quiz.practiceUrl} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', marginBottom: 10,
              padding: '12px 14px', borderRadius: 'var(--radius)', textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem',
              border: '1px solid var(--hp-gold)', background: 'rgba(201,168,76,0.08)', color: 'var(--hp-gold)' }}>
            <Smartphone size={15} /> {T(lang, 'practice')}
          </a>
        )}
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={!!translating} onClick={beginQuestions}><Play size={15} /> {T(lang, 'start')}</button>
      </div>
    );
  }

  // ── Take-flow: questions ────────────────────────────────────────────────────
  if (view === 'quiz' && quiz) {
    const q = quiz.questions[qIdx];
    return (
      <div style={{ width: '100%', position: 'relative' }}>
        {flashWrong && <WrongFlash lang={lang} />}
        <BackBar onClick={backToList} label={T(lang, 'quit')} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontWeight: 800, color: 'var(--hp-gold)', fontSize: '0.8rem' }}>{quiz.topic}</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{T(lang, 'question')(qIdx + 1, quiz.questions.length)}</span>
        </div>
        {/* progress bar */}
        <div style={{ height: 6, borderRadius: 999, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ height: '100%', width: `${(qIdx / quiz.questions.length) * 100}%`, background: 'var(--hp-gold)', transition: 'width 0.3s' }} />
        </div>

        <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--hp-text)', marginBottom: 18, lineHeight: 1.4 }}>{pick(q.prompt, contentLang)}</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {currentOptions.map((opt, i) => (
            <button key={i} onClick={() => answer(opt)} disabled={submitting}
              style={{ textAlign: 'left', padding: '14px 16px', borderRadius: 'var(--radius)', cursor: 'pointer',
                border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)',
                fontSize: '0.92rem', lineHeight: 1.35, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 999, border: '1px solid var(--hp-border)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--hp-gold)', fontSize: '0.8rem' }}>
                {String.fromCharCode(65 + i)}
              </span>
              {pick(opt.o.text, contentLang)}
            </button>
          ))}
        </div>
        {submitting && <div style={{ marginTop: 16, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={14} /> {T(lang, 'issuing')}</div>}
      </div>
    );
  }

  // ── Take-flow: passed ───────────────────────────────────────────────────────
  if (view === 'passed' && quiz) {
    return (
      <div style={{ width: '100%', textAlign: 'center', padding: '10px 4px' }}>
        <div style={{ width: 72, height: 72, borderRadius: 999, background: 'rgba(46,125,50,0.12)', border: '2px solid var(--hp-green)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <Award size={36} color="var(--hp-green)" />
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>{T(lang, 'passedTitle')}</div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.9rem', margin: '8px 0 4px' }}>{T(lang, 'passedBody')(quiz.topic)}</p>
        {passResult?.certNo && <p style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{T(lang, 'certNo')} {passResult.certNo}</p>}
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={backToList}>{T(lang, 'backToTraining')}</button>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  const outstanding = (data.assignments || []).filter((a) => a.status === 'outstanding');
  const passedQuizIds = new Set((data.certificates || []).map((c) => c.quizId));
  const requiredQuizzes = outstanding
    .map((a) => ({ assignment: a, quiz: (data.quizzes || []).find((q) => q.id === a.quizId) }))
    .filter((x) => x.quiz);
  const requiredIds = new Set(requiredQuizzes.map((x) => x.quiz.id));
  const library = (data.quizzes || []).filter((q) => !requiredIds.has(q.id));

  return (
    <div style={{ width: '100%' }}>
      <Title icon={<GraduationCap size={18} color="var(--hp-gold)" />} text="Training" />
      {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.8rem', marginBottom: 12 }}>{error}</div>}

      {requiredQuizzes.length > 0 && (
        <Section label="Required for you">
          {requiredQuizzes.map(({ assignment, quiz: q }) => (
            <QuizRow key={assignment.id} q={q} required onStart={() => startQuiz(q)} note={`Assigned ${fmtWhen(assignment.assignedAt)}`} />
          ))}
        </Section>
      )}

      <Section label="Available training">
        {!library.length && <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', padding: '8px 0' }}>No training available right now.</div>}
        {library.map((q) => (
          <QuizRow key={q.id} q={q} passed={passedQuizIds.has(q.id)} onStart={() => startQuiz(q)}
            note={passedQuizIds.has(q.id) ? 'Certified — you can take it again anytime' : `${q.questions.length} questions`} />
        ))}
      </Section>

      <Section label="My certificates">
        {!(data.certificates || []).length && <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', padding: '8px 0' }}>No certificates yet. Pass a quiz to earn one.</div>}
        {(data.certificates || []).map((c) => (
          <div key={c.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 10, background: 'var(--hp-surface)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <Award size={18} color="var(--hp-gold)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>Competency — {c.topic}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 3 }}>Passed {fmtWhen(c.passedAt)} · {c.certNo}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => openCert(c, 'view')} disabled={busyId === c.id + 'view'} className="btn btn-primary btn-sm"><Eye size={13} /> {busyId === c.id + 'view' ? '…' : 'View'}</button>
              <button onClick={() => openCert(c, 'dl')} disabled={busyId === c.id + 'dl'} className="btn btn-secondary btn-sm"><Download size={13} /> {busyId === c.id + 'dl' ? '…' : 'Download'}</button>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}

// ── little pieces ─────────────────────────────────────────────────────────────
function WrongFlash({ lang }) {
  return (
    <>
      <style>{`@keyframes hpWrongPulse{0%{opacity:0}12%{opacity:1}100%{opacity:1}}`}</style>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'var(--hp-red, #d64545)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
        color: '#fff', textAlign: 'center', padding: 24, animation: 'hpWrongPulse 0.25s ease-out' }}>
        <AlertTriangle size={64} color="#fff" />
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.4rem', letterSpacing: '0.04em' }}>{T(lang, 'wrong')}</div>
        <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{T(lang, 'again')}</div>
      </div>
    </>
  );
}

function QuizRow({ q, onStart, required, passed, note }) {
  return (
    <div style={{ border: `1px solid ${required ? 'var(--hp-red)' : 'var(--hp-border)'}`, borderRadius: 'var(--radius)', padding: 14, marginBottom: 10,
      background: required ? 'rgba(214,69,69,0.05)' : 'var(--hp-surface)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 150 }}>
        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--hp-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {q.topic}
          {required && <span style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 999, background: 'var(--hp-red)', color: '#fff' }}>REQUIRED</span>}
          {passed && !required && <CheckCircle size={14} color="var(--hp-green)" />}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 3 }}>{note}</div>
      </div>
      <button className={required ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={onStart}>
        <Play size={13} /> {required ? 'Start now' : (passed ? 'Retake' : 'Start')}
      </button>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>{label}</span>
        <span style={{ flex: 1, height: 1, background: 'var(--hp-border)' }} />
      </div>
      {children}
    </div>
  );
}

function Title({ icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      {icon}
      <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>{text}</span>
    </div>
  );
}

function BackBar({ onClick, label = 'Back' }) {
  return (
    <button onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, marginBottom: 12, padding: 0 }}>
      <ChevronLeft size={16} /> {label}
    </button>
  );
}
